import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, SITE } from "@/data/site";
import { LogoInline } from "@/components/layout/Logo";

const linkCls = ({ isActive }) =>
  `relative px-3 py-2 text-[14px] font-medium transition-colors duration-200 ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`;

const MobileMenu = ({ onClose }) => (
  <motion.div
    id="mobile-menu"
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.25 }}
    className="absolute inset-x-0 top-full border-b border-white/10 bg-[#030712] lg:hidden"
    data-testid="mobile-menu"
  >
    <nav aria-label="Mobile" className="container-q flex flex-col py-4">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
          className={({ isActive }) => `border-b border-white/5 py-4 text-lg font-semibold ${isActive ? "text-white" : "text-slate-300"}`}>
          {n.label}
        </NavLink>
      ))}
      <Link to="/contact" onClick={onClose} data-testid="mobile-nav-cta" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3.5 text-[15px] font-semibold text-slate-950">
        Discuss Your Project <ArrowUpRight className="h-4 w-4" />
      </Link>
      <a href={`mailto:${SITE.email}`} className="mt-4 py-2 text-center text-sm text-slate-300" data-testid="mobile-nav-email">{SITE.email}</a>
    </nav>
  </motion.div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || open ? "border-white/10 bg-[#030712]/95 backdrop-blur-xl" : "border-transparent bg-[#030712]/40 backdrop-blur-md"}`}>
      <div className={`container-q flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-16" : "h-[76px]"}`}>
        <LogoInline />
        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <motion.span layoutId="nav-underline" className="grad-line absolute inset-x-3 -bottom-0.5 h-px" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-cta" className="group hidden items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[14px] font-semibold text-slate-950 transition-[background-color,transform] duration-200 hover:bg-cyan-100 active:scale-[0.98] sm:inline-flex">
            Discuss Your Project
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white lg:hidden" data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </header>
  );
};
